import { useMemo } from "react";
import { ChevronLeft, Tag as TagIcon, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ENTITY_GROUP_LABELS } from "@/constants/entityGroups";
import type { CollectionEntityEntry } from "@/utils/entityMappers";
import { CAPTION, CTA, LABEL, META, ROW_TEXT, ROW_TITLE } from "@/lib/typography";

interface EntityConfirmationStepProps {
  entities: CollectionEntityEntry[];
  selectedEntityIds: Set<string>;
  onToggleEntity: (entityId: string) => void;
  onToggleAll: () => void;
  onToggleGroup?: (entityIds: string[]) => void;
  stashCount: number;
  collectionTitle: string;
  onAdd: () => void;
  onBack: () => void;
  isPending: boolean;
  isLoading?: boolean;
  disabledEntityIds?: Set<string>;
}

export function EntityConfirmationStep({
  entities,
  selectedEntityIds,
  onToggleEntity,
  onToggleAll,
  onToggleGroup,
  stashCount,
  collectionTitle,
  onAdd,
  onBack,
  isPending,
  isLoading,
  disabledEntityIds,
}: EntityConfirmationStepProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, Array<{ entity_id: string; name: string; subtitle?: string }>>();
    for (const entry of entities) {
      const list = map.get(entry.group) ?? [];
      list.push({ entity_id: entry.entity_id, name: entry.name, subtitle: entry.subtitle });
      map.set(entry.group, list);
    }
    return map;
  }, [entities]);

  const selectedCount = selectedEntityIds.size;
  const allSelected = entities.length > 0 && selectedCount === entities.length;
  const stashLabel = stashCount === 1 ? "stash" : "stashes";

  const addLabel = (() => {
    if (isPending) return "Saving...";
    if (stashCount === 0) {
      if (selectedCount > 0) {
        return `Save ${selectedCount} ${selectedCount === 1 ? "discovery" : "discoveries"}`;
      }
      return "Done";
    }
    if (selectedCount > 0) {
      const totalCount = stashCount + selectedCount;
      return `Save ${totalCount} items`;
    }
    return `Save ${stashCount} ${stashLabel}`;
  })();

  const isEmpty = !isLoading && entities.length === 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={onBack}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        {isEmpty ? (
          <div className="flex-1 min-w-0">
            <h3 className={cn(ROW_TITLE, "truncate text-left")}>
              Save to {collectionTitle}
            </h3>
            <p className={cn(CAPTION, "text-left")}>
              No new discoveries found
            </p>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={onToggleAll}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleAll(); } }}
            className="flex-1 min-w-0 flex items-center justify-between gap-3 cursor-pointer"
          >
            <div className="min-w-0">
              <h3 className={cn(ROW_TITLE, "truncate text-left")}>
                {stashCount === 0 ? "New discoveries" : `Save to ${collectionTitle}`}
              </h3>
              <p className={cn(CAPTION, "text-left")}>
                {stashCount === 0 ? "Save to collection?" : "Include all discoveries?"}
              </p>
            </div>
            <Switch
              checked={allSelected}
              tabIndex={-1}
              className="pointer-events-none shrink-0"
            />
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pb-2">
        {isLoading && entities.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isEmpty ? (
          <div className={cn(META, "flex flex-col items-center justify-center py-12 text-center gap-2")}>
            <p>No extra discoveries this time, but {stashCount === 1 ? "your stash is" : "your stashes are"} ready to go.</p>
            <p className="text-foreground font-medium">Hit save and they're in your collection.</p>
          </div>
        ) : (
          <>
            {/* Entity groups */}
            {Array.from(grouped.entries()).map(([group, items]) => {
              const rawMeta = ENTITY_GROUP_LABELS[group] ?? { label: group, icon: TagIcon };
              const meta = group === "events" ? { ...rawMeta, label: "Upcoming Events" } : rawMeta;
              const Icon = meta.icon;
              const selectableItems = disabledEntityIds
                ? items.filter((i) => !disabledEntityIds.has(i.entity_id))
                : items;
              const groupAllSelected =
                selectableItems.length > 0 &&
                selectableItems.every((i) => selectedEntityIds.has(i.entity_id));
              return (
                <div key={group}>
                  {onToggleGroup && selectableItems.length > 0 ? (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => onToggleGroup(selectableItems.map((i) => i.entity_id))}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleGroup(selectableItems.map((i) => i.entity_id)); } }}
                      className="flex items-center justify-between w-full mb-1.5 px-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className={cn(LABEL, "text-muted-foreground uppercase tracking-wide")}>
                          {items.length} {meta.label}
                        </span>
                      </div>
                      <Switch
                        checked={groupAllSelected}
                        tabIndex={-1}
                        className="pointer-events-none scale-75"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-1.5 px-1">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className={cn(LABEL, "text-muted-foreground uppercase tracking-wide")}>
                        {items.length} {meta.label}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5">
                    {items.map((item) => {
                      const isDisabled = disabledEntityIds?.has(item.entity_id) ?? false;
                      const checked = selectedEntityIds.has(item.entity_id);

                      if (isDisabled) {
                        return (
                          <div
                            key={item.entity_id}
                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 opacity-50"
                          >
                            <Check className="h-4 w-4 text-positive shrink-0" />
                            <div className="min-w-0 flex-1">
                              <span className={cn(ROW_TEXT, "truncate block")}>{item.name}</span>
                              {item.subtitle && <span className={cn(CAPTION, "truncate block")}>{item.subtitle}</span>}
                            </div>
                            <span className={cn(CAPTION, "ml-auto shrink-0")}>Added</span>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={item.entity_id}
                          role="button"
                          tabIndex={0}
                          onClick={() => onToggleEntity(item.entity_id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleEntity(item.entity_id); } }}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer",
                            "hover:bg-accent/50 active:bg-accent",
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            tabIndex={-1}
                            className="pointer-events-none"
                          />
                          <div className="min-w-0 flex-1">
                            <span className={cn(ROW_TEXT, "truncate block")}>{item.name}</span>
                            {item.subtitle && <span className={cn(CAPTION, "truncate block")}>{item.subtitle}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Single CTA */}
      <div
        className="shrink-0 z-30 border-t bg-background px-4 py-3 flex justify-center overflow-hidden"
      >
        <Button
          variant="gradient"
          size="sm"
          onClick={onAdd}
          disabled={isPending}
          className="cursor-pointer max-w-full truncate transition-none"
        >
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
